// Tiny synchronous identity state for the title UI. Network login, signing and
// relay publishing live in deferred modules; reading a callsign must not pull
// those systems onto the blocking flight entry path.

const GUEST_KEY = 'takemetoyourledger:guest:v1';
const MODE_KEY = 'takemetoyourledger:identity-mode:v1';

export interface GuestRecord {
  nsecHex: string;
  pubkey?: string;
  name: string;
  createdAt: number;
  v: 1;
}

function bytesToHex(bytes: Uint8Array): string {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return bytes;
}

function freshSecretHex(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
}

export function loadGuestRecord(): GuestRecord {
  try {
    const raw = localStorage.getItem(GUEST_KEY);
    if (raw) {
      // Legacy format was the bare secret hex.
      if (/^[0-9a-f]{64}$/.test(raw)) {
        const rec: GuestRecord = { nsecHex: raw, name: '', createdAt: Date.now(), v: 1 };
        localStorage.setItem(GUEST_KEY, JSON.stringify(rec));
        return rec;
      }
      const rec = JSON.parse(raw) as GuestRecord;
      if (/^[0-9a-f]{64}$/.test(rec.nsecHex)) return rec;
    }
  } catch { /* storage unavailable or corrupt */ }
  const rec: GuestRecord = { nsecHex: freshSecretHex(), name: '', createdAt: Date.now(), v: 1 };
  try { localStorage.setItem(GUEST_KEY, JSON.stringify(rec)); } catch { /* ignore */ }
  return rec;
}

export function saveGuestRecord(rec: GuestRecord): void {
  try { localStorage.setItem(GUEST_KEY, JSON.stringify(rec)); } catch { /* ignore */ }
}

export function getGuestName(): string {
  return loadGuestRecord().name;
}

export function setGuestName(name: string): void {
  const rec = loadGuestRecord();
  rec.name = name.slice(0, 16);
  saveGuestRecord(rec);
}

/** Synchronous best effort for menu highlighting. New and legacy records are
 * populated the first time they sign; until then the board simply has no `me`
 * highlight rather than downloading crypto on the title screen. */
export function cachedGuestPubkey(): string | null {
  const pubkey = loadGuestRecord().pubkey;
  return pubkey && /^[0-9a-f]{64}$/.test(pubkey) ? pubkey : null;
}

export type IdentityMode = 'guest' | 'nostr';

export function getIdentityMode(): IdentityMode {
  try {
    if (localStorage.getItem(MODE_KEY) === 'nostr') return 'nostr';
  } catch { /* ignore */ }
  return 'guest';
}

export function setIdentityMode(mode: IdentityMode): void {
  try { localStorage.setItem(MODE_KEY, mode); } catch { /* ignore */ }
}
